import { useSPAContext } from "@/providers/spa-provider";
import { useDexList } from "@/hooks/queries/use-dex";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { SimpleButton } from "@/components/ui/buttons/simple-button";
import { RouteNames } from "@/types/route-names";
import type { DexSetSummary } from "types/web-app";

const rewardLabel = (set: DexSetSummary) => {
  if (set.reward.type === "coin") return `${set.reward.amount} coins`;
  if (set.reward.type === "keys") return `${set.reward.amount} key${set.reward.amount !== 1 ? "s" : ""}`;
  return "Exclusive pack";
};

export const DexHomeView = () => {
  const { setCurrentRoute, setViewedDexSetId } = useSPAContext();
  const { data, isLoading, isError, refetch } = useDexList();

  const handleSetClick = (setId: string) => {
    setViewedDexSetId(setId);
    setCurrentRoute(RouteNames.DEX_DETAILS);
  };

  const claimableSets = data?.sets.filter((s) => s.claimable) ?? [];
  const almostSets = data?.sets
    .filter((s) => !s.claimable && !s.claimed && s.obtained_count > 0)
    .sort((a, b) => b.obtained_count / b.total - a.obtained_count / a.total)
    .slice(0, 3) ?? [];

  return (
    <div className="w-full p-1 flex flex-col gap-3 h-full overflow-y-auto">
      <StandardFrame variant="purple">
        <div className="flex flex-col gap-3">
        {isLoading ? (
          <div className="text-center text-white/50 py-8">Loading...</div>
        ) : isError ? (
          <div className="flex flex-col items-center gap-2 text-center text-white/50 py-8">
            <span>Failed to load dex.</span>
            <SimpleButton variant="gray" onClick={() => refetch()}>Retry</SimpleButton>
          </div>
        ) : (
          <>
            <div className="text-center text-white font-bold text-lg">
              {data?.total_species ?? 0} species registered
            </div>

            {claimableSets.length > 0 && (
              <section>
                <h3 className="text-pk-yellow-1 font-black text-sm uppercase tracking-wider mb-2">
                  Claimable ({claimableSets.length})
                </h3>
                <div className="flex flex-col gap-2">
                  {claimableSets.map((set) => (
                    <SetCard key={set.id} set={set} onClick={() => handleSetClick(set.id)} />
                  ))}
                </div>
              </section>
            )}

            {almostSets.length > 0 && (
              <section>
                <h3 className="text-white/70 font-black text-sm uppercase tracking-wider mb-2">
                  Almost there
                </h3>
                <div className="flex flex-col gap-2">
                  {almostSets.map((set) => (
                    <SetCard key={set.id} set={set} onClick={() => handleSetClick(set.id)} />
                  ))}
                </div>
              </section>
            )}

            <section>
              <h3 className="text-white/50 font-black text-sm uppercase tracking-wider mb-2">
                All sets
              </h3>
              <div className="flex flex-col gap-2">
                {data?.sets.map((set) => (
                  <SetCard key={set.id} set={set} onClick={() => handleSetClick(set.id)} />
                ))}
              </div>
            </section>
          </>
        )}
        </div>
      </StandardFrame>
    </div>
  );
};

const SetCard = ({ set, onClick }: { set: DexSetSummary; onClick: () => void }) => (
  <div
    className="bg-pk-gray-2 border-2 border-pk-gray-4 rounded-lg p-3 flex items-center justify-between gap-2 cursor-pointer hover:bg-pk-gray-1 active:scale-95 transition-all"
    onClick={onClick}
  >
    <div className="flex flex-col gap-0.5">
      <span className="text-white font-bold text-sm">{set.display_name}</span>
      <span className="text-white/60 text-xs">
        {set.obtained_count}/{set.total}
        {set.claimed && " · Claimed"}
      </span>
    </div>
    <div className="flex flex-col items-end gap-1">
      <div className="w-24 h-2 bg-pk-gray-4 rounded-full overflow-hidden">
        <div
          className="h-full bg-pk-blue-4 rounded-full transition-all"
          style={{ width: `${Math.min(100, (set.obtained_count / set.total) * 100)}%` }}
        />
      </div>
      {set.claimable && (
        <span className="text-pk-yellow-1 font-black text-xs animate-pulse">CLAIM!</span>
      )}
      {!set.claimable && !set.claimed && (
        <span className="text-white/40 text-xs">{rewardLabel(set)}</span>
      )}
    </div>
  </div>
);
